import React from "react";
import { Modal, Badge, DataTable as Table } from "../../../components/ui";
import { fmtDate, fmt } from "../../../lib/formatters";
import { getCustomerDisplayId } from "../../../constants/globalConstants";
import { Phone, MapPin, User as UserIcon } from "lucide-react";

export const MaterialRequestDetailModal = ({ selReq, onClose, projects, user, onOwnerAction, onApproveRequest, isInventoryModule, inventoryMaster = [] }) => {
  const isAuthorized = user?.role === "owner" || user?.role === "admin" || user?.role === "solar-admin";
  const [receivedQtys, setReceivedQtys] = React.useState({});
  const [confirmFullDelivery, setConfirmFullDelivery] = React.useState(false);

  // If it's a regular sale (Retail Sale)
  const isSale = selReq?.recordType === 'sale';

  // Determine if it's a delivery confirmation scenario (Project Manager view)
  const isDeliveryConfirm = !isInventoryModule && !isSale && (selReq?.status === "dispatched" || selReq?.status === "partially_delivered");
  
  const handleConfirmDelivery = () => {
    if (confirmFullDelivery) {
      return onOwnerAction(String(selReq._id || selReq.id), "delivered");
    }
    const itemsWithQty = Object.keys(receivedQtys).filter(k => receivedQtys[k] > 0);
    if (itemsWithQty.length > 0) {
      onOwnerAction(String(selReq._id || selReq.id), "delivered", { deliveredItemsQtys: receivedQtys });
      setReceivedQtys({});
    }
  };

  const headers = isSale 
    ? ["Material", "Quantity", "Rate", "Total"]
    : isDeliveryConfirm 
      ? ["Receive Now", "Material", "Req Details", "Expected Shipment"] 
      : ["Material", "Requested", "Delivered", "Pending Dispatch", "Stock Status"];

  const matchedProj = Array.isArray(projects) ? projects.find(p => 
    (p._id?.toString() === selReq?.projectId?.toString()) || 
    (p.id?.toString() === selReq?.projectId?.toString()) || 
    (p.projectId === selReq?.projectId)
  ) : null;
  const proj = matchedProj || selReq?.projectDetails;

  return (
    <Modal isOpen={!!selReq} onClose={onClose} title={isSale ? "Retail Sale Details" : "Material Request Details"} size="max-w-3xl">
      {selReq && (
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Badge 
                status={selReq.status} 
                label={selReq.status === 'owner_approved' && (selReq.approvedBy?.role === 'admin' || selReq.approvedBy?.role === 'solar-admin') ? 'Admin Approved' : undefined}
              />
              <div className="px-2 py-0.5 rounded-full bg-brand-orange/5 border border-brand-orange/10 text-brand-orange font-bold text-[10px] tracking-tight">
                {isSale ? (selReq.invoiceNo || 'RETAIL') : (getCustomerDisplayId(proj) || "—")}
              </div>
            </div>
            <div className="text-right">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{fmtDate(selReq.createdAt || selReq.date)}</div>
                <div className="text-[10px] text-slate-500 font-bold uppercase">
                  {isSale ? (typeof selReq.customer === 'object' ? selReq.customer.name : selReq.customer) : proj?.customerName}
                </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(selReq.projectDetails || isSale) && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                  <UserIcon size={10} /> {isSale ? 'Customer Info' : 'Customer Info'}
                </div>
                <div className="space-y-1.5">
                   <div className="flex items-center gap-2">
                      <Phone size={12} className="text-slate-400" />
                      <span className="text-xs font-bold text-slate-700">
                        {isSale ? (selReq.customerPhone || "No phone") : (proj?.phoneNumber || "No phone available")}
                      </span>
                   </div>
                   {isSale && selReq.customerEmail && (
                     <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Email</span>
                        <span className="text-xs font-bold text-slate-700">{selReq.customerEmail}</span>
                     </div>
                   )}
                   {isSale && selReq.reference && (
                     <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Ref</span>
                        <span className="text-[10px] font-bold text-slate-700 uppercase px-2 py-0.5 bg-slate-200 rounded">{selReq.reference}</span>
                     </div>
                   )}
                   {!isSale && (
                       <div className="flex items-start gap-2">
                          <MapPin size={12} className="text-slate-400 mt-0.5 shrink-0" />
                          <span className="text-xs font-semibold text-slate-600 leading-relaxed italic line-clamp-2">
                            {proj?.address || proj?.location || "No address available"}
                          </span>
                       </div>
                   )}
                </div>
              </div>
            )}

            {isSale && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                  <MapPin size={10} /> Billing Address
                </div>
                <div className="text-xs font-medium text-slate-600 leading-relaxed">
                  {selReq.billingAddressLine1 ? (
                    <>
                      <div>{selReq.billingAddressLine1}</div>
                      {selReq.billingAddressLine2 && <div>{selReq.billingAddressLine2}</div>}
                      <div>{[selReq.billingCity, selReq.billingState, selReq.billingPincode].filter(Boolean).join(', ')}</div>
                    </>
                  ) : (
                    <span className="italic text-slate-400">Walk-in / No Billing Address</span>
                  )}
                </div>
              </div>
            )}

            {isSale && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                  🚚 Delivery Details
                </div>
                <div className="space-y-1">
                  {selReq.deliveryContactPerson && (
                    <div className="text-xs font-bold text-slate-700">{selReq.deliveryContactPerson} <span className="text-slate-500 font-medium ml-1">{selReq.deliveryPhone}</span></div>
                  )}
                  <div className="text-xs font-medium text-slate-600 leading-relaxed">
                    {selReq.deliveryAddressLine1 ? (
                      <>
                        <div>{selReq.deliveryAddressLine1}</div>
                        {selReq.deliveryAddressLine2 && <div>{selReq.deliveryAddressLine2}</div>}
                        <div>{[selReq.deliveryCity, selReq.deliveryState, selReq.deliveryPincode].filter(Boolean).join(', ')}</div>
                      </>
                    ) : (
                      <span className="italic text-slate-400">No Delivery Address</span>
                    )}
                  </div>
                  {(selReq.courierName || selReq.trackingNumber) && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                         <div className="text-[10px] font-bold text-slate-600">
                             Courier: {selReq.courierName || 'N/A'} <span className="mx-1">•</span> Track: {selReq.trackingNumber || 'N/A'}
                         </div>
                      </div>
                  )}
                </div>
              </div>
            )}

            {!isSale && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                  <UserIcon size={10} /> Department / Team
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-700 capitalize">
                    {selReq.installationTrade ? selReq.installationTrade.replace(/_/g, ' ') : 'General'}
                  </div>
                  <div className="text-[10px] font-medium text-slate-500 mt-0.5">
                    {proj?.projectManager?.name ? `PM: ${proj.projectManager.name}` : 'No PM assigned'}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
            <Table headers={headers}>
              {selReq.items.map((it, i) => {
                const itemData = it.item || it; // For sales, it is the item itself
                const requestedQty = Number(it.quantity || it.qty || it.requiredQty || 0);
                const deliveredQty = Number(it.deliveredQty || 0);
                
                // Live Stock Lookup
                const masterItem = inventoryMaster.find(mi => 
                  mi._id?.toString() === (itemData._id || itemData.id || it.itemId)?.toString() ||
                  mi.name?.toLowerCase().trim() === (it.itemName || itemData.name)?.toLowerCase().trim()
                );
                const availableQty = masterItem ? Number(masterItem.availableQty || 0) : Number(itemData.availableQty || 0);
                
                const itemId = String(itemData._id || itemData.id || i);
                const isFullyDelivered = deliveredQty >= requestedQty;

                if (isSale) {
                  return (
                    <tr key={i} className="hover:bg-slate-50/50">
                      <td className="td font-semibold text-brand-dark text-[13px]">
                        {it.itemName || itemData.name || "Unknown Item"}
                        <div className="text-[9px] text-slate-400 font-bold uppercase">{itemData.unit || 'units'}</div>
                      </td>
                      <td className="td font-bold text-brand-dark text-[13px]">{it.qty}</td>
                      <td className="td">
                          <div className="font-bold text-slate-600 text-[11px]">{fmt(it.rate)}</div>
                          {it.discountPercent > 0 && <div className="text-[9px] text-green-600 font-bold">-{it.discountPercent}% OFF</div>}
                      </td>
                      <td className="td font-black text-brand-blue text-[13px]">
                          {fmt(it.amount)}
                          {it.taxPercent > 0 && <div className="text-[9px] text-slate-400 font-bold">+{it.taxPercent}% GST</div>}
                      </td>
                    </tr>
                  );
                }

                if (isDeliveryConfirm) {
                    return (
                        <tr key={i} className={`hover:bg-slate-50/50 ${isFullyDelivered ? 'opacity-50' : ''}`}>
                          <td className="td p-2 text-center w-28">
                             {!isFullyDelivered ? (
                                <input 
                                    type="number"
                                    min="0"
                                    disabled={confirmFullDelivery}
                                    max={Math.max(0, requestedQty - deliveredQty)}
                                    placeholder={confirmFullDelivery ? "Auto Max" : `Max ${Math.max(0, requestedQty - deliveredQty)}`}
                                    className={`w-full text-center p-1.5 text-xs font-bold border border-slate-200 rounded-lg focus:outline-none focus:border-brand-orange transition-all font-mono text-brand-dark ${confirmFullDelivery ? 'bg-slate-50 cursor-not-allowed text-slate-400' : ''}`}
                                    value={confirmFullDelivery ? Math.max(0, requestedQty - deliveredQty) : (receivedQtys[itemId] !== undefined ? receivedQtys[itemId] : '')}
                                    onChange={(e) => {
                                        const valStr = e.target.value;
                                        if (valStr === '') {
                                            setReceivedQtys(prev => {
                                                const newQtys = { ...prev };
                                                delete newQtys[itemId];
                                                return newQtys;
                                            });
                                            return;
                                        }
                                        const val = Number(valStr);
                                        const maxAllowed = requestedQty - deliveredQty;
                                        setReceivedQtys(prev => ({
                                            ...prev,
                                            [itemId]: val > maxAllowed ? maxAllowed : (val < 0 ? 0 : val)
                                        }));
                                    }}
                                />
                             ) : (
                                <div className="text-emerald-500 font-bold text-lg">✓</div>
                             )}
                          </td>
                          <td className="td font-semibold text-brand-dark text-[13px]">
                            {itemData.name || "Unknown Item"}
                            <div className="text-[9px] text-slate-400 font-bold uppercase">{itemData.unit || 'units'}</div>
                          </td>
                          <td className="td">
                            <span className="text-[11px] font-bold block text-slate-700">Req: {requestedQty}</span>
                            {deliveredQty > 0 && <span className="text-[10px] font-bold text-amber-600 block mt-0.5">Prev Rec: {deliveredQty}</span>}
                          </td>
                          <td className="td">
                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${isFullyDelivered ? 'bg-emerald-50 text-emerald-600' : 'bg-brand-blue/10 text-brand-blue'}`}>
                                {isFullyDelivered ? 'None' : `Expected: ${Math.max(0, requestedQty - deliveredQty)}`}
                            </span>
                          </td>
                        </tr>
                    )
                }

                const hasStock = availableQty >= (requestedQty - deliveredQty);
                return (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="td font-semibold text-brand-dark text-[13px]">
                      {it.itemName || itemData.name || itemData.itemName || "Unknown Item"}
                      <div className="text-[9px] text-slate-400 font-bold uppercase">{itemData.unit || itemData.units || 'units'}</div>
                    </td>
                    <td className="td font-bold text-brand-dark text-[13px]">{requestedQty}</td>
                    <td className="td">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded ${isFullyDelivered ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'}`}>
                            {deliveredQty}
                        </span>
                    </td>
                    <td className="td">
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${isFullyDelivered ? 'text-emerald-500' : 'text-amber-600 bg-amber-50 border border-amber-200'}`}>
                            {!isFullyDelivered ? `${Math.max(0, requestedQty - deliveredQty)} missing` : 'All met'}
                        </span>
                    </td>
                    <td className="td">
                      <div className="flex flex-col">
                        <span className={`text-[10px] font-bold ${hasStock ? "text-green-600" : "text-red-500"}`}>
                          Stock: {availableQty}
                        </span>
                        <span className={`text-[9px] font-black tracking-widest uppercase ${hasStock ? "text-green-500" : "text-red-400"}`}>
                          {hasStock ? "Available" : "Shortage"}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </Table>
          </div>

          {isSale && (
            <div className="space-y-3">
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                    <div className="flex flex-col md:flex-row justify-between gap-6">
                        <div className="flex-1 space-y-2 text-xs">
                            <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                                <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Taxable Amount</span>
                                <span className="font-bold text-slate-700">{fmt(selReq.taxableAmount || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                                <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Total Tax (GST)</span>
                                <span className="font-bold text-slate-700">{fmt(selReq.totalTax || 0)}</span>
                            </div>
                            {selReq.totalDiscount > 0 && (
                                <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                                    <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Total Discount</span>
                                    <span className="font-bold text-green-600">-{fmt(selReq.totalDiscount || 0)}</span>
                                </div>
                            )}
                            {selReq.charges && selReq.charges.length > 0 && (
                                <div className="pb-2 border-b border-slate-200/50">
                                    <div className="font-bold text-slate-500 uppercase tracking-wider text-[10px] mb-1">Additional Charges</div>
                                    {selReq.charges.map((c, i) => (
                                        <div key={i} className="flex justify-between items-center pl-2 mb-1">
                                            <span className="font-medium text-slate-600 text-[11px]">{c.name}</span>
                                            <span className="font-bold text-slate-700 text-[11px]">+{fmt(c.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selReq.adjustment !== 0 && (
                                <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                                    <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Adjustment</span>
                                    <span className={`font-bold ${selReq.adjustment < 0 ? 'text-green-600' : 'text-slate-700'}`}>{fmt(selReq.adjustment)}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center pt-1">
                                <span className="font-black text-slate-600 uppercase tracking-wider text-[11px]">Net Bill</span>
                                <span className="font-black text-slate-800 text-sm">{fmt(selReq.netBill || 0)}</span>
                            </div>
                        </div>

                        <div className="w-full md:w-64 flex flex-col justify-end gap-3 p-4 bg-white rounded-lg border border-slate-100 shadow-sm">
                            <div className="flex justify-between items-center">
                                <span className="font-black text-slate-400 uppercase tracking-widest text-[10px]">Paid Amount</span>
                                <span className="font-black text-emerald-600 text-lg">{fmt(selReq.paidAmount || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-black text-slate-400 uppercase tracking-widest text-[10px]">Balance Due</span>
                                <span className="font-black text-red-500 text-lg">{fmt(selReq.balanceDue || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                                <span className="font-black text-brand-dark uppercase tracking-widest text-[11px]">Net Payable</span>
                                <span className="font-black text-brand-blue text-2xl">{fmt(selReq.netPayable || 0)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Payment History</span>
                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">{selReq.payments?.length || 0} Records</span>
                    </div>
                    {selReq.payments && selReq.payments.length > 0 ? (
                        <div className="max-h-40 overflow-y-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50/50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-[13px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                                        <th className="px-4 py-2 text-[13px] font-bold text-slate-400 uppercase tracking-widest">Mode / Details</th>
                                        <th className="px-4 py-2 text-[13px] font-bold text-slate-400 uppercase tracking-widest text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {selReq.payments.map((p, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/50">
                                            <td className="px-4 py-2 text-[13px] font-medium text-slate-600">
                                                {p.date ? new Date(p.date).toLocaleString() : 'N/A'}
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="text-[11px] font-bold text-brand-dark uppercase">
                                                    {p.method?.replace('_', ' ')}
                                                    {p.transactionId && <span className="ml-1 text-slate-400 font-normal">({p.transactionId})</span>}
                                                </div>
                                                {(p.bankName || p.chequeNumber || p.receiverName) && (
                                                    <div className="text-[9px] font-medium text-slate-500 mt-0.5">
                                                        {[p.bankName, p.chequeNumber, p.receiverName].filter(Boolean).join(' - ')}
                                                    </div>
                                                )}
                                                {p.remarks && (
                                                    <div className="text-[9px] text-slate-400 italic mt-0.5">Note: {p.remarks}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-[13px] font-black text-emerald-600 text-right">
                                                +{fmt(p.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-6 flex flex-col items-center justify-center text-center">
                            <div className="text-slate-400 mb-2">💸</div>
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">No Payments Yet</div>
                            <div className="text-[10px] text-slate-400 mt-1">This invoice has no recorded payments.</div>
                        </div>
                    )}
                </div>
            </div>
          )}

          {!isSale && selReq.notes && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Request Notes</div>
              <div className="text-xs text-slate-600 italic">"{selReq.notes}"</div>
            </div>
          )}

          {!isSale && (selReq.status === "dispatched" || selReq.status === "delivered" || selReq.status === "fulfilled" || selReq.status === "partially_delivered") && (
            <div className="bg-green-50 rounded-2xl p-4 border border-green-100 shadow-sm relative overflow-hidden">
              <div className="font-bold text-[10px] text-green-800 uppercase tracking-widest mb-3">
                {selReq.status === "delivered" ? "✅ Materials Delivered & Received" : "🚚 Dispatch Information"}
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div><span className="text-green-600/70 block mb-0.5 font-bold uppercase tracking-tighter text-[9px]">Driver Name</span><span className="font-bold text-green-900">{selReq.dispatchDetails?.driverName || selReq.driverName || 'N/A'}</span></div>
                <div><span className="text-green-600/70 block mb-0.5 font-bold uppercase tracking-tighter text-[9px]">Vehicle Number</span><span className="font-bold text-green-900 tracking-wider uppercase">{selReq.dispatchDetails?.vehicleNumber || selReq.vehicleNo || 'N/A'}</span></div>
                {(selReq.dispatchDetails?.notes || selReq.description) && (
                  <div className="col-span-2 mt-2 pt-2 border-t border-green-200/50">
                    <span className="text-green-600/70 block mb-0.5 font-bold uppercase tracking-tighter text-[9px]">Description / Notes</span>
                    <span className="text-green-900 italic font-medium">{selReq.dispatchDetails?.notes || selReq.description}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {!isSale && (
              <>
                {/* Owner/Admin approval logic for AWAITING_OWNER_APPROVAL or PARTIALLY_DELIVERED */}
                {['AWAITING_OWNER_APPROVAL', 'PARTIALLY_DELIVERED'].includes(String(selReq.status).toUpperCase()) && isAuthorized && (
                  <>
                    <button 
                      className={`btn-primary flex-1 py-3 ${String(selReq.status).toUpperCase() === "PARTIALLY_DELIVERED" ? "bg-amber-500 hover:bg-amber-600 border-amber-500 shadow-amber-200" : ""}`} 
                      onClick={() => onOwnerAction(String(selReq._id || selReq.id), "owner_approved")}
                    >
                      {String(selReq.status).toUpperCase() === "PARTIALLY_DELIVERED" ? "🔄 Resend Missing Items" : "Approve Request"}
                    </button>
                    {String(selReq.status).toUpperCase() === "PARTIALLY_DELIVERED" && (
                      <button 
                        className="btn-primary flex-1 py-3 bg-emerald-600 border-emerald-600 hover:bg-emerald-700 shadow-emerald-200" 
                        onClick={() => { if(window.confirm('Are you sure you want to finalize this request? Unreceived items will be ignored and cost will be calculated for received items only.')) { onOwnerAction(String(selReq._id || selReq.id), "fulfilled"); } }}
                      >
                        ✅ Accept Partial As Final
                      </button>
                    )}
                    <button 
                      className="btn-secondary flex-1 border-red-200 text-red-600 px-3 hover:bg-red-50" 
                      onClick={() => onOwnerAction(String(selReq._id || selReq.id), "rejected")}
                    >
                      Reject
                    </button>
                  </>
                )}
              </>
            )}
            {!isSale && (String(selReq.status).toUpperCase() === "APPROVED") && (user?.role === "inventory_manager" || user?.role === "store-manager" || user?.role === "admin" || user?.role === "solar-admin") && (
              <button className="btn-orange flex-1 py-3" onClick={() => onApproveRequest(String(selReq._id || selReq.id))}>🚚 Dispatch Materials</button>
            )}
            {!isSale && isDeliveryConfirm && (
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-3 p-4 bg-orange-50/50 border border-brand-orange/20 rounded-2xl group cursor-pointer hover:bg-orange-50 transition-all"
                     onClick={() => setConfirmFullDelivery(!confirmFullDelivery)}>
                   <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
                     confirmFullDelivery ? 'bg-brand-orange border-brand-orange' : 'bg-white border-brand-orange/30'
                   }`}>
                     {confirmFullDelivery && <div className="w-1.5 h-1.5 bg-white rounded-full shadow-sm" />}
                   </div>
                   <div>
                     <div className="text-xs font-black text-brand-orange uppercase tracking-wider">Confirm Full Delivery</div>
                     <div className="text-[10px] text-slate-500 font-medium tracking-tight">I confirm all items in this request have been received.</div>
                   </div>
                </div>

                {(() => {
                    const pendingItems = selReq.items.filter(it => (it.deliveredQty || 0) < (it.qty || it.requiredQty));
                    const allEntriesValid = pendingItems.every((it, idx) => {
                        const itemId = String(it.item?._id || it.item?.id || idx);
                        const val = receivedQtys[itemId];
                        return val !== undefined && val >= 1;
                    });
                    const canSubmit = confirmFullDelivery || allEntriesValid;
                    
                    return (
                        <button 
                          disabled={!canSubmit}
                          className={`btn-primary w-full py-4 bg-brand-orange border-brand-orange hover:bg-brand-orange/90 flex items-center justify-center gap-2 shadow-lg shadow-orange-100 disabled:opacity-30 disabled:grayscale disabled:shadow-none transition-all`} 
                          onClick={handleConfirmDelivery}
                        >
                          <div className="w-5 h-5 bg-white/20 rounded-lg flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          </div>
                          {confirmFullDelivery 
                            ? "✅ Confirm Full Receipt" 
                            : allEntriesValid 
                              ? `✅ Receive Entered Quantities` 
                              : "Enter all quantities to proceed"}
                        </button>
                    );
                })()}
              </div>
            )}
            {(isSale || String(selReq.status).toUpperCase() === "PENDING") && (
              <button className="btn-secondary flex-1 py-3" onClick={onClose}>Close Details</button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};


